import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Visitors in Pakistan see PKR and can order. Everyone else sees the USD price
// the admin entered and enquires over WhatsApp instead of checking out, so the
// currency also decides which buying flow the storefront offers.
//
// The choice is made by the system alone - there is no manual switch.
export const PKR = "PKR";
export const USD = "USD";

const CACHE_KEY = "kinetix_geo";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const GEO_TIMEOUT_MS = 5000;

// Our own backend resolves the country. Going through the server rather than
// calling a geo API from the page avoids CORS, and avoids the bot challenges
// those providers put in front of browser traffic - a direct call from here
// simply gets refused.
const GEO_URL = `${process.env.REACT_APP_BE_URL || ""}/api/geo`;

const readStore = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null; // private mode / storage disabled
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable is not worth breaking the page over */
  }
};

// Used for the first paint, and as the fallback when the lookup cannot answer:
// a browser in Pakistan almost always reports the Karachi timezone or a -PK
// locale.
export const guessFromBrowser = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (zone === "Asia/Karachi") return PKR;
  } catch (e) {
    /* Intl unavailable */
  }
  const languages = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  if (languages.some((lang) => String(lang).toUpperCase().endsWith("-PK"))) return PKR;
  return USD;
};

const readCachedCountry = () => {
  const raw = readStore(CACHE_KEY);
  if (!raw) return null;
  try {
    const cached = JSON.parse(raw);
    if (!cached || typeof cached.country !== "string") return null;
    if (Date.now() - cached.at > CACHE_TTL_MS) return null;
    return cached.country;
  } catch (e) {
    return null;
  }
};

// Resolves the visitor's country, cached for a day. Returns null when it
// cannot be determined - on a local address, or if the backend is unreachable -
// so the caller keeps the browser guess instead of forcing a wrong currency.
export const lookupCountry = async () => {
  const cached = readCachedCountry();
  if (cached) return cached;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
  try {
    const response = await fetch(GEO_URL, { signal: controller.signal });
    if (!response.ok) return null;
    const data = await response.json();
    const country = String(data && data.country ? data.country : "").toUpperCase();
    if (!country) return null;
    writeStore(CACHE_KEY, JSON.stringify({ country, at: Date.now() }));
    return country;
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const CurrencyContext = createContext({
  currency: PKR,
  isPakistan: true,
  resolved: false,
});

export const CurrencyProvider = ({ children }) => {
  // Start on the browser's own signals so the first paint is already close,
  // then correct it once the country comes back.
  const [currency, setCurrency] = useState(guessFromBrowser);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    lookupCountry().then((country) => {
      if (cancelled) return;
      if (country) setCurrency(country === "PK" ? PKR : USD);
      setResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ currency, isPakistan: currency === PKR, resolved }),
    [currency, resolved]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);

const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

// Shown wherever a price has not been set for the visitor's currency. Prices
// are never converted between currencies - each one is whatever the admin
// typed into that column - so a missing USD price simply has no figure to show.
export const PRICE_ON_REQUEST = "Contact for price";

// Reads the column for the visitor's currency, nothing more. `available` is
// false when that column is empty, and the caller shows PRICE_ON_REQUEST.
export const resolveAmount = (pkrValue, usdValue, currency) => {
  const value = currency === USD ? toAmount(usdValue) : toAmount(pkrValue);
  return {
    amount: value,
    currency: currency === USD ? USD : PKR,
    available: value !== null,
  };
};

export const formatMoney = (amount, currency) => {
  const value = Number(amount) || 0;
  const fixed = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return currency === USD ? `$ ${fixed}` : `Rs ${fixed}`;
};

// Renders a resolved price, or the contact line when that currency has none.
// Accepts both shapes produced upstream: a single price ({ amount }) from
// getUnitPrice/getShipping, and a range ({ min }) from getPriceRange.
export const formatPrice = (resolved) => {
  if (!resolved || !resolved.available) return PRICE_ON_REQUEST;
  const value = resolved.amount === undefined || resolved.amount === null ? resolved.min : resolved.amount;
  if (value === undefined || value === null) return PRICE_ON_REQUEST;
  return formatMoney(value, resolved.currency);
};
